import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useVerificationModalStore } from "@/components/email-verification-modal";
import { openUpgradeModalFromError } from "@/components/upgrade-modal";
import { env } from "@/env";
import { isOnCooldown, sendVerificationEmail } from "@/lib/api/resend-verification";
import { getAccessToken } from "@/lib/auth/storage";
import { useAuthStore } from "@/stores/auth-store";
import { authClient } from "../auth-client";
import type { components } from "../types";

type AgentInstanceResponse = components["schemas"]["AgentInstanceResponse"];
type TaskResponse = components["schemas"]["TaskResponse"];
type RunResponse = components["schemas"]["RunResponse"];
type TaskWebhookInfoResponse = components["schemas"]["TaskWebhookInfoResponse"];
type TaskWebhookRotateResponse = components["schemas"]["TaskWebhookRotateResponse"];
type TaskWebhookUpdate = components["schemas"]["TaskWebhookUpdate"];
type MateWebhookInfoResponse = components["schemas"]["MateWebhookInfoResponse"];
type MateWebhookRotateResponse = components["schemas"]["MateWebhookRotateResponse"];
type MateWebhookUpdate = components["schemas"]["MateWebhookUpdate"];

/**
 * Fetch all agent instances for the current user
 */
export function useAgents() {
	return useQuery({
		queryKey: ["agents"],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/agents/instances/", {
				params: {
					query: {
						include_disabled: true,
					},
				},
			});

			if (error) {
				throw new Error("Failed to fetch agents");
			}

			return data as AgentInstanceResponse[];
		},
	});
}

/**
 * Fetch a single agent instance by ID
 */
export function useAgent(agentId: number, enabled = true) {
	return useQuery({
		queryKey: ["agent", agentId],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/agents/instances/{instance_id}", {
				params: {
					path: {
						instance_id: agentId,
					},
				},
			});

			if (error) {
				throw new Error("Failed to fetch agent");
			}

			return data as AgentInstanceResponse;
		},
		enabled: enabled && !!agentId,
	});
}

/**
 * Create a new agent instance
 */
export function useCreateAgent() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (body: components["schemas"]["AgentInstanceCreate"]) => {
			const { data, error } = await authClient.POST("/api/v1/agents/instances/", {
				body,
			});

			if (error) {
				throw new Error("Failed to create agent");
			}

			return data as AgentInstanceResponse;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["agents"] });
		},
	});
}

/**
 * Update an agent instance
 */
export function useUpdateAgent(agentId: number) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (updates: components["schemas"]["AgentInstanceUpdate"]) => {
			const { data, error } = await authClient.PATCH("/api/v1/agents/instances/{instance_id}", {
				params: { path: { instance_id: agentId } },
				body: updates,
			});

			if (error) {
				throw new Error("Failed to update agent");
			}

			return data as AgentInstanceResponse;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["agents"] });
			queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
		},
	});
}

/**
 * Enable an agent instance
 */
export function useEnableAgent(agentId: number) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			const { data, error } = await authClient.POST(
				"/api/v1/agents/instances/{instance_id}/enable",
				{
					params: { path: { instance_id: agentId } },
				}
			);

			if (error) {
				throw new Error("Failed to enable agent");
			}

			return data as AgentInstanceResponse;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["agents"] });
			queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
		},
	});
}

/**
 * Disable an agent instance
 */
export function useDisableAgent(agentId: number) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			const { data, error } = await authClient.POST(
				"/api/v1/agents/instances/{instance_id}/disable",
				{
					params: { path: { instance_id: agentId } },
				}
			);

			if (error) {
				throw new Error("Failed to disable agent");
			}

			return data as AgentInstanceResponse;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["agents"] });
			queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
		},
	});
}

/**
 * Archive an agent instance (soft delete)
 */
export function useArchiveAgent(agentId: number) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			const { error } = await authClient.DELETE("/api/v1/agents/instances/{instance_id}", {
				params: { path: { instance_id: agentId } },
			});

			if (error) {
				throw new Error("Failed to archive agent");
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["agents"] });
			queryClient.invalidateQueries({ queryKey: ["agent", agentId] });
		},
	});
}

/**
 * Fetch a single task by ID
 */
export function useTask(taskId: number, enabled = true) {
	return useQuery({
		queryKey: ["task", taskId],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/tasks/{task_id}", {
				params: { path: { task_id: taskId } },
			});
			if (error) throw new Error("Failed to fetch task");
			return data as TaskResponse;
		},
		enabled: enabled && !!taskId,
	});
}

/**
 * Fetch webhook configuration for a task.
 */
export function useTaskWebhook(taskId: number, enabled = true) {
	return useQuery({
		queryKey: ["task-webhook", taskId],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/tasks/{task_id}/webhook", {
				params: { path: { task_id: taskId } },
			});
			if (error) throw new Error("Failed to fetch task webhook");
			return data as TaskWebhookInfoResponse;
		},
		enabled: enabled && !!taskId,
	});
}

/**
 * Update webhook enabled state for a task.
 */
export function useUpdateTaskWebhook() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ taskId, updates }: { taskId: number; updates: TaskWebhookUpdate }) => {
			const { data, error } = await authClient.PATCH("/api/v1/tasks/{task_id}/webhook", {
				params: { path: { task_id: taskId } },
				body: updates,
			});
			if (error) throw new Error("Failed to update task webhook");
			return data as TaskWebhookInfoResponse;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
			queryClient.invalidateQueries({ queryKey: ["task-webhook", variables.taskId] });
		},
	});
}

/**
 * Rotate webhook token for a task and return full URL once.
 */
export function useRotateTaskWebhook() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (taskId: number) => {
			const { data, error } = await authClient.POST("/api/v1/tasks/{task_id}/webhook/rotate", {
				params: { path: { task_id: taskId } },
			});
			if (error) throw new Error("Failed to rotate task webhook");
			return data as TaskWebhookRotateResponse;
		},
		onSuccess: (_data, taskId) => {
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
			queryClient.invalidateQueries({ queryKey: ["task-webhook", taskId] });
		},
	});
}

/**
 * Fetch webhook configuration for a mate.
 */
export function useMateWebhook(instanceId: number, enabled = true) {
	return useQuery({
		queryKey: ["mate-webhook", instanceId],
		queryFn: async () => {
			const { data, error } = await authClient.GET(
				"/api/v1/agents/instances/{instance_id}/webhook",
				{ params: { path: { instance_id: instanceId } } }
			);
			if (error) throw new Error("Failed to fetch mate webhook");
			return data as MateWebhookInfoResponse;
		},
		enabled: enabled && !!instanceId,
	});
}

/**
 * Update webhook enabled state for a mate.
 */
export function useUpdateMateWebhook() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			instanceId,
			updates,
		}: {
			instanceId: number;
			updates: MateWebhookUpdate;
		}) => {
			const { data, error } = await authClient.PATCH(
				"/api/v1/agents/instances/{instance_id}/webhook",
				{ params: { path: { instance_id: instanceId } }, body: updates }
			);
			if (error) throw new Error("Failed to update mate webhook");
			return data as MateWebhookInfoResponse;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ["agents"] });
			queryClient.invalidateQueries({ queryKey: ["mate-webhook", variables.instanceId] });
		},
	});
}

/**
 * Rotate webhook token for a mate and return full URL once.
 */
export function useRotateMateWebhook() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (instanceId: number) => {
			const { data, error } = await authClient.POST(
				"/api/v1/agents/instances/{instance_id}/webhook/rotate",
				{ params: { path: { instance_id: instanceId } } }
			);
			if (error) throw new Error("Failed to rotate mate webhook");
			return data as MateWebhookRotateResponse;
		},
		onSuccess: (_data, instanceId) => {
			queryClient.invalidateQueries({ queryKey: ["agents"] });
			queryClient.invalidateQueries({ queryKey: ["mate-webhook", instanceId] });
		},
	});
}

/**
 * Fetch all tasks for the current user
 */
export function useTasks() {
	return useQuery({
		queryKey: ["tasks"],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/tasks/", {
				params: {
					query: {
						include_disabled: true,
						include_archived: true,
					},
				},
			});

			if (error) {
				throw new Error("Failed to fetch tasks");
			}

			return data as TaskResponse[];
		},
	});
}

/**
 * Fetch runs for a specific agent
 */
export function useAgentRuns(agentId: number | undefined, limit = 100) {
	return useQuery({
		queryKey: ["agent-runs", agentId, limit],
		queryFn: async () => {
			if (!agentId) {
				return [];
			}

			const { data, error } = await authClient.GET("/api/v1/runs/agents/{agent_id}/runs", {
				params: {
					path: {
						agent_id: agentId,
					},
					query: {
						limit,
					},
				},
			});

			if (error) {
				throw new Error("Failed to fetch agent runs");
			}

			return data as RunResponse[];
		},
		enabled: !!agentId,
		staleTime: 30_000,
		refetchInterval: (query) => {
			const runs = query.state.data ?? [];
			const hasActive = runs.some(
				(run) =>
					run.status === "running" ||
					run.status === "in_progress" ||
					run.status === "awaiting_approval"
			);
			return hasActive ? 10_000 : false;
		},
	});
}

/**
 * Fetch all user runs with infinite scroll pagination
 */
export function useUserRuns(pageSize = 20) {
	return useInfiniteQuery({
		queryKey: ["user-runs"],
		queryFn: async ({ pageParam = 0 }) => {
			const { data, error } = await authClient.GET("/api/v1/runs/", {
				params: {
					query: {
						limit: pageSize,
						offset: pageParam,
					},
				},
			});

			if (error) {
				throw new Error("Failed to fetch user runs");
			}

			return data as RunResponse[];
		},
		getNextPageParam: (lastPage, allPages) => {
			// If last page has fewer items than pageSize, we're done
			if (lastPage.length < pageSize) {
				return undefined;
			}
			// Calculate next offset
			return allPages.length * pageSize;
		},
		initialPageParam: 0,
		staleTime: 2 * 60 * 1000,
		refetchOnWindowFocus: false,
		refetchInterval: (query) => {
			const runs = query.state.data?.pages.flat() ?? [];
			const hasActive = runs.some(
				(run) => run.status === "running" || run.status === "awaiting_approval"
			);
			return hasActive ? 10_000 : false;
		},
	});
}

/**
 * Fetch runs for a specific task (with conditional fetching)
 */
export function useTaskRuns(taskId: number | undefined, enabled = true) {
	return useQuery({
		queryKey: ["task-runs", taskId],
		queryFn: async () => {
			if (!taskId) {
				return [];
			}

			const { data, error } = await authClient.GET("/api/v1/runs/", {
				params: {
					query: {
						task_id: taskId,
						limit: 10,
						offset: 0,
					},
				},
			});

			if (error) {
				throw new Error("Failed to fetch task runs");
			}

			return data as RunResponse[];
		},
		enabled: !!taskId && enabled,
	});
}

async function postTaskExecution(path: string): Promise<Record<string, unknown>> {
	const token = getAccessToken();
	if (!token) throw new Error("Not authenticated");

	const response = await fetch(`${env.VITE_API_BASE_URL}${path}`, {
		method: "POST",
		headers: { Authorization: `Bearer ${token}` },
	});

	if (!response.ok) {
		const errorData = (await response.json().catch(() => ({}))) as {
			detail?: unknown;
			error_code?: unknown;
			runs_remaining?: unknown;
		};
		if (response.status === 402) {
			openUpgradeModalFromError(errorData as Record<string, unknown>);
		}
		if (response.status === 403 && errorData.error_code === "EMAIL_VERIFICATION_REQUIRED") {
			const runsRemaining =
				typeof errorData.runs_remaining === "number" ? errorData.runs_remaining : 0;
			const email = useAuthStore.getState().user?.email;
			let emailSent = false;
			if (email && !isOnCooldown(email)) {
				const result = await sendVerificationEmail(email);
				emailSent = result.success;
			}
			useVerificationModalStore.getState().open(runsRemaining, emailSent);
		}
		const detail =
			typeof errorData.detail === "string"
				? errorData.detail
				: `HTTP ${response.status}: ${response.statusText}`;
		throw new Error(detail);
	}

	return (await response.json()) as Record<string, unknown>;
}

/**
 * Run a task in the background via POST /tasks/{id}/start.
 * Returns run_id immediately; execution continues server-side.
 * The caller can navigate to /runs/{runId} to watch progress via GET /runs/{id}/stream.
 */
export function useRunTask() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (taskId: number) => {
			const data = await postTaskExecution(`/api/v1/tasks/${taskId}/start`);
			return { runId: data.run_id as number };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["task-runs"] });
			queryClient.invalidateQueries({ queryKey: ["agent-runs"] });
			queryClient.invalidateQueries({ queryKey: ["user-runs"] });
		},
	});
}

/**
 * Update a task
 */
export function useUpdateTask(taskId: number) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (updates: components["schemas"]["TaskUpdate"]) => {
			const { data, error } = await authClient.PATCH("/api/v1/tasks/{task_id}", {
				params: { path: { task_id: taskId } },
				body: updates,
			});

			if (error) {
				throw new Error("Failed to update task");
			}

			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
		},
	});
}

/**
 * Update a task by ID (useful for board interactions outside edit modal state).
 */
export function useUpdateTaskById() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			taskId,
			updates,
		}: {
			taskId: number;
			updates: components["schemas"]["TaskUpdate"];
		}) => {
			const { data, error } = await authClient.PATCH("/api/v1/tasks/{task_id}", {
				params: { path: { task_id: taskId } },
				body: updates,
			});

			if (error) {
				throw new Error("Failed to update task");
			}

			return data as TaskResponse;
		},
		onSuccess: (updatedTask) => {
			queryClient.setQueryData(["tasks"], (current: TaskResponse[] | undefined) => {
				if (!current) return current;
				return current.map((task) => (task.id === updatedTask.id ? updatedTask : task));
			});
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
		},
	});
}

/**
 * Create a new task for an existing agent
 */
export function useCreateTask() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (taskData: components["schemas"]["TaskCreate"]) => {
			const { data, error } = await authClient.POST("/api/v1/tasks/", {
				body: taskData,
			});

			if (error) {
				throw new Error("Failed to create task");
			}

			return data as TaskResponse;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
		},
	});
}

/**
 * Move a task to queue and immediately start execution.
 */
export function useEnqueueTask() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (taskId: number) => {
			const data = await postTaskExecution(`/api/v1/tasks/${taskId}/enqueue`);
			return data as components["schemas"]["TaskEnqueueResponse"];
		},
		onSuccess: (result) => {
			queryClient.setQueryData(["tasks"], (current: TaskResponse[] | undefined) => {
				if (!current) return current;
				return current.map((task) => (task.id === result.task.id ? result.task : task));
			});
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
			queryClient.invalidateQueries({ queryKey: ["task-runs"] });
			queryClient.invalidateQueries({ queryKey: ["agent-runs"] });
			queryClient.invalidateQueries({ queryKey: ["user-runs"] });
		},
	});
}

/**
 * Delete a task
 */
export function useDeleteTask() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (taskId: number) => {
			const { error } = await authClient.DELETE("/api/v1/tasks/{task_id}", {
				params: { path: { task_id: taskId } },
			});

			if (error) {
				throw new Error("Failed to delete task");
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
		},
	});
}

/**
 * Archive a run (soft delete)
 */
export function useArchiveRun() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (runId: number) => {
			const { data, error } = await authClient.POST("/api/v1/runs/{run_id}/archive", {
				params: { path: { run_id: runId } },
			});

			if (error) {
				throw new Error("Failed to archive run");
			}

			return data as RunResponse;
		},
		onSuccess: () => {
			// Invalidate all run queries to refresh lists
			queryClient.invalidateQueries({ queryKey: ["task-runs"] });
			queryClient.invalidateQueries({ queryKey: ["agent-runs"] });
			queryClient.invalidateQueries({ queryKey: ["user-runs"] });
		},
	});
}

/**
 * Fetch recent runs across all agents (non-paginated, for building latestRunByTaskId maps)
 */
export function useRecentRuns(limit = 100) {
	return useQuery({
		queryKey: ["recent-runs", limit],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/runs/", {
				params: { query: { limit, offset: 0 } },
			});
			if (error) throw new Error("Failed to fetch recent runs");
			return data as RunResponse[];
		},
		staleTime: 2 * 60 * 1000,
	});
}

/**
 * Lightweight freshness check — polls GET /runs/check every 30s and
 * invalidates run queries only when data actually changes (new run created,
 * run archived, etc.). Enables near-real-time sidebar updates for runs
 * created externally (triggers, schedules, other tabs).
 *
 * Why this exists: useUserRuns only polls while active runs are visible.
 * When all runs are terminal, polling stops entirely. This hook fills
 * that gap with a tiny (~50 byte) check that never stops.
 *
 * Assumptions:
 * - Mounted once in NavHistory (polls only while sidebar is rendered)
 * - Errors are handled by TanStack Query's global retry/Sentry config
 * - First fetch sets baseline only (no invalidation)
 */
export function useRunListFreshness() {
	const queryClient = useQueryClient();
	const prevRef = useRef<{ total_count: number; latest_run_id: number | null } | undefined>(
		undefined
	);

	const { data } = useQuery({
		queryKey: ["run-freshness"],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/runs/check");
			if (error) throw new Error("Failed to check run freshness");
			return data as components["schemas"]["RunFreshnessResponse"];
		},
		refetchInterval: 30_000,
		staleTime: 30_000,
	});

	useEffect(() => {
		if (!data) return;
		const prev = prevRef.current;
		prevRef.current = {
			total_count: data.total_count,
			latest_run_id: data.latest_run_id ?? null,
		};
		if (!prev) return; // First fetch — nothing to compare
		if (
			prev.total_count !== data.total_count ||
			prev.latest_run_id !== (data.latest_run_id ?? null)
		) {
			queryClient.invalidateQueries({ queryKey: ["user-runs"] });
			queryClient.invalidateQueries({ queryKey: ["recent-runs"] });
			queryClient.invalidateQueries({ queryKey: ["agent-runs"] });
		}
	}, [data, queryClient]);
}

/**
 * Fetch all schedules for the current user
 */
export function useSchedules() {
	return useQuery({
		queryKey: ["schedules"],
		queryFn: async () => {
			const { data, error } = await authClient.GET("/api/v1/schedules");

			if (error) {
				throw new Error("Failed to fetch schedules");
			}

			return data;
		},
	});
}

/**
 * Create a schedule for an existing task
 */
export function useCreateSchedule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (scheduleData: components["schemas"]["ScheduleCreate"]) => {
			const { data, error } = await authClient.POST("/api/v1/schedules", {
				body: scheduleData,
			});

			if (error) {
				throw new Error("Failed to create schedule");
			}

			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["schedules"] });
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
		},
	});
}

/**
 * Delete a schedule by schedule_id (database ID)
 */
export function useDeleteSchedule() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (scheduleId: number) => {
			const { error } = await authClient.DELETE("/api/v1/schedules/{schedule_id}", {
				params: { path: { schedule_id: scheduleId } },
			});

			if (error) {
				throw new Error("Failed to delete schedule");
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["schedules"] });
			queryClient.invalidateQueries({ queryKey: ["tasks"] });
		},
	});
}
